/**
 * The prompt-type vocabulary: one definition of the label, the colour and the explanation.
 *
 * There were three copies before this — a switch inside QueryTypeBadge, a hand-written option list
 * in the create-prompt modal, and the filter dropdown, which had no copy at all and simply printed
 * the raw enum ("aspectSpecificUnaidedAwareness"). So the same prompt type appeared under three
 * different names on one screen, and the colour coding that makes the table scannable stopped at
 * the table.
 *
 * Keys are the camelCase values the API sends. The backend's global
 * ConditionalJsonStringEnumConverter(CamelCase) serialises every enum this way — never compare
 * against the PascalCase spelling in the C# QueryType enum, and never against a number.
 */

/**
 * `label` names the type; `badgeLabel` is the compact form for a table cell; `tooltip` explains what
 * the measurement actually is.
 *
 * The tooltips carry the one distinction that decides whether a number means anything: whether the
 * prompt names the brand. An unaided prompt must not — that is what makes it evidence of
 * discovery. A reputation prompt must, which is why it can never tell you whether anyone would have
 * found you. Reading a reputation score as visibility is the mistake this copy exists to prevent.
 */
export const QUERY_TYPES = {
  unaidedAwareness: {
    label: "Unaided awareness",
    badgeLabel: "Unaided",
    color: "green",
    variant: "light",
    tooltip:
      "The prompt does not name your brand — it asks the kind of question a buyer would ask. " +
      "It measures whether the model brings you up on its own, which is the closest thing to " +
      "being discovered. The hardest bar of the four, and the one worth watching.",
  },
  aspectSpecificUnaidedAwareness: {
    label: "Unaided awareness, one aspect",
    badgeLabel: "Unaided · aspect",
    color: "green",
    variant: "outline",
    tooltip:
      "Unaided awareness narrowed to a single aspect, e.g. only questions about compliance. " +
      "Tells you which topics you are known for, rather than whether you are known at all.",
  },
  reputation: {
    label: "Reputation (aided)",
    badgeLabel: "Reputation",
    color: "purple",
    variant: "light",
    tooltip:
      "The prompt names your brand, so a mention is guaranteed and proves nothing about " +
      "visibility. What it measures is what the model says once you are already in the " +
      "conversation — the framing a buyer meets after they have heard of you.",
  },
  aspectSpecificReputation: {
    label: "Reputation, one aspect",
    badgeLabel: "Reputation · aspect",
    color: "purple",
    variant: "outline",
    tooltip:
      "Names your brand and narrows the question to one aspect, e.g. pricing or support. " +
      "Use it to find which specific claim about you the models get wrong.",
  },
  productRecommendation: {
    label: "Product recommendation",
    badgeLabel: "Recommendation",
    color: "teal",
    variant: "light",
    tooltip:
      "Asks for advice in a product category the way a buyer would — 'what should I use for…'. " +
      "Tracks whether you get recommended, not merely mentioned.",
  },
  custom: {
    label: "Custom",
    badgeLabel: "Custom",
    color: "orange",
    variant: "light",
    tooltip:
      "A prompt you or an agent wrote. Measured and reported like the others, but it is not one " +
      "of the standard types, so it is not comparable across brands.",
  },
};

/**
 * Display order. Unaided first because it is the metric that answers "are we found at all";
 * custom last because it is a bucket, not a measurement.
 *
 * Used by the filter dropdown and by the prompt-overview columns, so both read the same way round.
 */
export const QUERY_TYPE_ORDER = [
  "unaidedAwareness",
  "aspectSpecificUnaidedAwareness",
  "reputation",
  "aspectSpecificReputation",
  "productRecommendation",
  "custom",
];

/**
 * Metadata for a type, including ones this build has never heard of.
 *
 * Always returns something displayable. The previous switch fell through for an unrecognised value
 * and rendered a badge with an empty label and no colour — a blank chip where a type should be,
 * which reads as "no type" rather than "a type this UI does not know". A server that gains a new
 * QueryType before the frontend ships must degrade to showing the raw value.
 */
export function queryTypeMeta(queryType) {
  if (!queryType) return null;
  const known = QUERY_TYPES[queryType];
  if (known) return known;
  return {
    label: queryType,
    badgeLabel: queryType,
    color: "gray",
    variant: "light",
    tooltip: "This prompt type is newer than this page. It is still being measured normally.",
  };
}

/** Options for a Select, in display order, limited to the types actually present if asked. */
export function queryTypeOptions(presentValues) {
  const present = presentValues ? new Set(presentValues) : null;
  const ordered = QUERY_TYPE_ORDER.filter((value) => !present || present.has(value));
  // Anything the server sent that this build does not know about still has to be selectable,
  // otherwise a filter dropdown silently cannot reach a subset of the rows below it.
  const extra = present
    ? [...present].filter((value) => value && !QUERY_TYPES[value]).sort()
    : [];
  return [...ordered, ...extra].map((value) => ({
    value,
    label: queryTypeMeta(value).label,
  }));
}
